import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  InputRightAddon,
  Text,
} from '@chakra-ui/react'
import type { DiscountRequest, Offer, OfferDiscounts } from 'BrokerTsLib/src/dtos'
import { DiscountType, getGcpVoucherName } from 'BrokerTsLib/src/dtos'

import { useAuth } from '@/context/AuthContext'
import { useOffersContext } from '@/context/useOffersContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { useGetOfferAvailableDiscounts } from '@/hooks/useOffer'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { DiscountRequestState, Insurer } from 'BrokerTsLib/src/enums'
import type { UseFormReturn } from 'BrokerTsLib/src/rhf'
import { FormProvider } from 'BrokerTsLib/src/rhf'
import React, { useEffect, useState } from 'react'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { InputGroup } from '../InputGroup'
import { Loading } from '../Loading'
import { HelperTooltip } from '../ui/HelperTooltip'
import { QuoteItemDiscountRequestForm } from './QuoteItemDiscountRequestForm'

export const QuoteItemDiscountsForm = ({
  offer,
  formMethods
}:
{
  offer: Offer
  formMethods: UseFormReturn<OfferDiscounts, any, any>
}) => {

  const { translate } = useTranslationsContext()
  const { user } = useAuth()
  const allowDiscountsOverLimit = user?.allowDiscountsOverLimit ?? false
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas

  const { calculatingOffersIds } = useOffersContext()
  const isRecalculating = calculatingOffersIds?.includes(offer!.offerId) ?? false

  const {
    getValues,
    getFieldState,
    register,
    reset
  } = formMethods

  const [requestDiscountData, setRequestDiscountData] =
    useState<DiscountRequest>({
      state: DiscountRequestState.None,
      userCanApprove: false,
      discounts: [],
    })

  const { refetch: reloadDiscounts, data, isLoading: isLoadingDiscounts } = useGetOfferAvailableDiscounts(offer.offerId, { enabled: !isPublicPoleas })
  const discounts = data?.data.availableDiscounts
  const discountRequest = data?.data.discountRequest

  useEffect(() => {
    // po kazdem prenacteni slev se musi resetovat formular.
    reset({
      discounts: discounts ?? [],
    })

    if (allowDiscountsOverLimit && discountRequest) {
      setRequestDiscountData(discountRequest)
    }
  }, [discounts, discountRequest])

  const filteredDiscounts = discounts?.map((x, i) => { return { x, i } }).filter(x => x.x.editK2) ?? []
  const anyDiscounts = filteredDiscounts.length > 0

  const { isDisabled } = useInsuranceParams()

  useEffect(() => {
    if (!isRecalculating && !isPublicPoleas) {
      reloadDiscounts()// potreba refreshnout slevy, protoze se po kalkulaci mohly zmenit (napr. i v pripade pouziti a schvalovani nadlimitnich slev)
    }
  }, [isRecalculating])

  if (isLoadingDiscounts)
    return <Loading large={true} />

  return (
    <FormProvider  {...formMethods}>
      <Box as="form" sx={{ height: '100%' }}>
        <Grid
          sx={{
            height: '100%',
            rowGap: 4
          }}
        >
          <Grid
            sx={{
              height: '100%',
              width: '100%',
              gridTemplateColumns: { lg: 'repeat(2, 1fr)' },
              columnGap: 8,
              rowGap: 6,
            }}
          >
            {!anyDiscounts && <Text> {translate('quoteItem.discountsForm.noDiscounts', 'Žádné slevy nejsou k dispozici')}</Text>}

            {anyDiscounts &&
              <>
                {filteredDiscounts.map(disc => {
                  const discError = getFieldState(`discounts.${disc.i}.discount`)?.error
                  const voucherError = getFieldState(`discounts.${disc.i}.voucher`)?.error
                  const isKoopVoucher = offer.insurerId == Insurer.Kooperativa && disc.x.type == DiscountType.Voucher
                  const isColonnadeVoucher = offer.insurerId == Insurer.Colonnade && disc.x.type == DiscountType.Voucher
                  const isGcpVoucher = offer.insurerId == Insurer.GeneraliCeskaPojistovna &&
                    (disc.x.type == DiscountType.Voucher || disc.x.type == DiscountType.VoucherPov ||
                      disc.x.type == DiscountType.VoucherHav)
                  const isCppVoucher = offer.insurerId == Insurer.CPP &&
                    (disc.x.type == DiscountType.VoucherPov || disc.x.type == DiscountType.VoucherHav)
                  const voucherName = isGcpVoucher || isCppVoucher ? getGcpVoucherName(disc.x.type) : ''

                  return (
                    <React.Fragment key={disc.x.insuranceCategoryDiscountId}>

                      {!isColonnadeVoucher && !isGcpVoucher &&
                        <FormControl isInvalid={!!discError}>
                          <Flex sx={{ width: '100%', gap: 4, justifyContent: 'space-between' }}>
                            <FormLabel htmlFor={`discounts.${disc.i}.insuranceCategoryDiscountId`}>
                              {disc.x.name}&nbsp;<Text>(max. {disc.x.maxDiscount} {disc.x.unit})</Text>

                              {disc.x.description && <HelperTooltip label={disc.x.description} />}
                            </FormLabel>
                          </Flex>

                          <InputGroup
                            htmlSize={12}
                            type='text'
                            variant='outline'
                            errors={discError}
                            {...register(`discounts.${disc.i}.discount`, {
                              onChange: (e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, '')
                              }
                            })}
                            pattern="[0-9]*"
                            step={1}
                            sx={{ fontSize: 'md', fontWeight: 'bold' }}
                            isDisabled={isDisabled}
                            rightElement={<InputRightAddon>{disc.x.unit}</InputRightAddon>}
                          >
                          </InputGroup>
                        </FormControl>
                      }

                      {(isKoopVoucher || isColonnadeVoucher) &&
                        <FormControl isInvalid={!!voucherError}>
                          <Flex sx={{ width: '100%', gap: 4, justifyContent: 'space-between' }}>
                            <FormLabel>
                              {translate('quoteItem.discountsForm.voucherCode', 'Kód voucheru')}
                            </FormLabel>
                          </Flex>

                          <InputGroup
                            variant='outline'
                            {...register(`discounts.${disc.i}.voucher`)}
                            errors={voucherError}
                            //id={`disc_${disc.insuranceCategoryDiscountId}_voucher`}
                            sx={{ fontSize: 'md', fontWeight: 'bold' }}
                            isDisabled={isDisabled}
                          >
                          </InputGroup>
                        </FormControl>
                      }

                      {(isGcpVoucher || isCppVoucher) &&
                        <FormControl isInvalid={!!voucherError}>
                          <Flex sx={{ width: '100%', gap: 4, justifyContent: 'space-between' }}>
                            <FormLabel>
                              {voucherName}
                            </FormLabel>
                          </Flex>

                          <InputGroup
                            variant='outline'
                            {...register(`discounts.${disc.i}.voucher`)}
                            errors={voucherError}
                            sx={{ fontSize: 'md', fontWeight: 'bold' }}
                            isDisabled={isDisabled}
                          >
                          </InputGroup>
                        </FormControl>
                      }

                    </React.Fragment>
                  )
                })}
              </>
            }

          </Grid>

          {allowDiscountsOverLimit &&
            <QuoteItemDiscountRequestForm offerId={offer.offerId} requestData={requestDiscountData} />
          }

        </Grid>
      </Box>
    </FormProvider>
  )
}

